import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  Paper,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  Snackbar,
} from "@mui/material";
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Language as LanguageIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from "@mui/icons-material";
import api from "./api";
import { AVAILABLE_LANGUAGES, DEFAULT_LANGUAGE } from "./utils/languages";

// Consistent card styling
const cardStyle = {
  height: '340px', // Fixed height for all cards
  width: '100%',   // Take full width of grid item
  display: 'flex',
  flexDirection: 'column',
  transition: 'box-shadow 0.3s ease-in-out, transform 0.2s',
  '&:hover': {
    boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
    transform: 'translateY(-5px)',
  },
};

const cardMediaStyle = {
  height: 180,  // Fixed height for media
  objectFit: 'contain',
  bgcolor: 'background.default',
  p: 2
};

const cardContentStyle = {
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
};

const detailCardMediaStyle = {
  height: 300,
  objectFit: 'contain',
  bgcolor: 'background.default',
  p: 2
};

export default function SearchCategoryManager() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [allCategories, setAllCategories] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [categoryData, setCategoryData] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState(DEFAULT_LANGUAGE);
  const [savingChanges, setSavingChanges] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, category: null });
  const [deleting, setDeleting] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [draggedItem, setDraggedItem] = useState(null);
  const [savingSortOrder, setSavingSortOrder] = useState(false);
  const [dropTarget, setDropTarget] = useState(null);
  const scrollContainerRef = React.useRef(null);

  // Default languages
  const defaultLanguages = AVAILABLE_LANGUAGES;

  // Fetch all categories on component mount
  useEffect(() => {
    fetchAllCategories();
  }, []);

  // Effect for handling ID in URL
  useEffect(() => {
    if (id) {
      if (allCategories.length > 0) {
        // If categories are loaded, find the one with matching ID
        const category = allCategories.find((cat) => cat.id === id);
        if (category) {
          setSelectedCategory(category);
          setCategoryData(category);
          setEditMode(false);
        } else {
          // If not found in local data, fetch from API
          fetchCategoryDetails(id);
        }
      } else {
        // If categories are not yet loaded, fetch directly
        fetchCategoryDetails(id);
      }
    } else {
      // Clear selected category when no ID is present
      setSelectedCategory(null);
      setCategoryData(null);
    }
  }, [id, allCategories]);

  // Frontend search based on query
  useEffect(() => {
    if (searchQuery.length < 1) {
      setSearchResults(allCategories);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filteredResults = allCategories.filter((category) => {
      // Check default name
      if (category.name.toLowerCase().includes(query)) {
        return true;
      }

      // Check language-specific names
      const languageSpec = category.languageSpec || {};
      for (const lang in languageSpec) {
        if (languageSpec[lang]?.name?.toLowerCase().includes(query)) {
          return true;
        }
      }

      return false;
    });

    setSearchResults(filteredResults);
  }, [searchQuery, allCategories]);

  // Fetch all categories
  const fetchAllCategories = async () => {
    setLoading(true);
    try {
      const response = await api.get("/category/categories");

      if (response.data && response.data.success) {
        const categories = response.data.data;

        // Parse languageSpec and slogan if they're strings for each category
        const processedCategories = categories.map((cat) => {
          const processed = { ...cat };
          
          if (typeof cat.languageSpec === "string") {
            processed.languageSpec = JSON.parse(cat.languageSpec);
          }
          
          if (typeof cat.slogan === "string") {
            try {
              processed.slogan = JSON.parse(cat.slogan);
            } catch (e) {
              // If parsing fails, treat as a legacy string slogan
              processed.slogan = { english: cat.slogan };
            }
          }
          
          return processed;
        });

        setAllCategories(processedCategories);
        setSearchResults(processedCategories);
      } else {
        console.error("Error fetching categories");
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch category details by ID (used as fallback if not in local data)
  const fetchCategoryDetails = async (categoryId) => {
    setLoading(true);
    try {
      const response = await api.get(`/category/${categoryId}`);

      if (response.data && response.data.success) {
        const categoryDetail = response.data.data;

        // Parse languageSpec if it's a string
        if (typeof categoryDetail.languageSpec === "string") {
          categoryDetail.languageSpec = JSON.parse(categoryDetail.languageSpec);
        }

        // Parse slogan if it's a string
        if (typeof categoryDetail.slogan === "string") {
          try {
            categoryDetail.slogan = JSON.parse(categoryDetail.slogan);
          } catch (e) {
            // If parsing fails, treat as a legacy string slogan
            categoryDetail.slogan = { english: categoryDetail.slogan };
          }
        }

        setCategoryData(categoryDetail);
        setSelectedCategory(categoryDetail);
        setEditMode(false);
      } else {
        console.error("Category not found or error in response");
      }
    } catch (error) {
      console.error("Error fetching category details:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle category selection from autocomplete
  const handleCategorySelect = (event, value) => {
    if (value && value.id) {
      navigate(`/category/${value.id}`);
    }
  };

  // Handle card click to navigate to category details
  const handleCardClick = (category) => {
    navigate(`/category/${category.id}`);
  };

  // Enable edit mode
  const handleEditClick = () => {
    setEditMode(true);
  };

  // Save language-specific name changes
  const handleSaveLanguageName = async () => {
    setSavingChanges(true);
    try {
      // Get the current language data
      const languageSpec = categoryData.languageSpec || {};
      const currentLanguageData = languageSpec[currentLanguage] || {};
      const newName = currentLanguageData.name || "";

      const response = await api.put(
        `/category/update-category/${categoryData.id}`,
        {
          language: currentLanguage,
          name: newName,
          sortingPriority: categoryData.sortingPriority,
          slogan: categoryData.slogan?.[currentLanguage] || "",
        }
      );

      if (response.data && response.data.success) {
        // Parse languageSpec and slogan if they're strings
        const updatedCategory = response.data.data;
        if (typeof updatedCategory.languageSpec === "string") {
          updatedCategory.languageSpec = JSON.parse(
            updatedCategory.languageSpec
          );
        }

        if (typeof updatedCategory.slogan === "string") {
          try {
            updatedCategory.slogan = JSON.parse(updatedCategory.slogan);
          } catch (e) {
            // If parsing fails, treat as a legacy string slogan
            updatedCategory.slogan = { english: updatedCategory.slogan };
          }
        }

        setEditMode(false);
        setSelectedCategory(updatedCategory);
        setCategoryData(updatedCategory);

        // Update the category in the allCategories array
        setAllCategories((prevCategories) =>
          prevCategories.map((cat) =>
            cat.id === updatedCategory.id ? updatedCategory : cat
          )
        );
      } else {
        console.error("Failed to update category item");
      }
    } catch (error) {
      console.error("Error saving changes:", error);
    } finally {
      setSavingChanges(false);
    }
  };

  // Handle delete category click
  const handleDeleteClick = () => {
    setDeleteDialog({ open: true, category: selectedCategory });
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!deleteDialog.category) return;
    
    setDeleting(true);
    try {
      await api.deleteCategory(deleteDialog.category.id);
      
      // Remove from local state
      setAllCategories(prev => prev.filter(cat => cat.id !== deleteDialog.category.id));
      setSearchResults(prev => prev.filter(cat => cat.id !== deleteDialog.category.id));
      
      // Show success notification
      setNotification({
        open: true,
        message: `Category "${deleteDialog.category.name}" deleted successfully!`,
        severity: 'success'
      });
      
      // Close dialog and reset selection
      setDeleteDialog({ open: false, category: null });
      setSelectedCategory(null);
      setCategoryData(null);
      setEditMode(false);
      
      // Navigate back to category list
      navigate('/categories');
      
    } catch (error) {
      console.error('Error deleting category:', error);
      
      // Extract error message from the API response
      let errorMessage = 'Failed to delete category. Please try again.';
      if (error.response?.data?.error?.userMessage) {
        errorMessage = error.response.data.error.userMessage;
      } else if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setNotification({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setDeleting(false);
    }
  };

  // Handle delete dialog close
  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, category: null });
  };

  // Handle notification close
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // Handle language name input change
  const handleLanguageNameChange = (value) => {
    const updatedLanguageSpec = { ...categoryData.languageSpec } || {};

    if (!updatedLanguageSpec[currentLanguage]) {
      updatedLanguageSpec[currentLanguage] = {};
    }

    updatedLanguageSpec[currentLanguage].name = value;

    setCategoryData({
      ...categoryData,
      languageSpec: updatedLanguageSpec,
    });
  };

  // Handle slogan input change
  const handleSloganChange = (value) => {
    const updatedSloganSpec = { ...(categoryData.slogan || {}) };
    updatedSloganSpec[currentLanguage] = value;

    setCategoryData({
      ...categoryData,
      slogan: updatedSloganSpec,
    });
  };

  // Handle language image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("language", currentLanguage);

      const response = await api.put(
        `/upload-image/upload-category-image/${categoryData.id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data && response.data.success) {
        // Parse languageSpec and slogan if they're strings
        const updatedCategory = response.data.data;
        if (typeof updatedCategory.languageSpec === "string") {
          updatedCategory.languageSpec = JSON.parse(
            updatedCategory.languageSpec
          );
        }

        if (typeof updatedCategory.slogan === "string") {
          try {
            updatedCategory.slogan = JSON.parse(updatedCategory.slogan);
          } catch (e) {
            // If parsing fails, treat as a legacy string slogan
            updatedCategory.slogan = { english: updatedCategory.slogan };
          }
        }

        setCategoryData(updatedCategory);
        setSelectedCategory(updatedCategory);

        // Update the category in the allCategories array
        setAllCategories((prevCategories) =>
          prevCategories.map((cat) =>
            cat.id === updatedCategory.id ? updatedCategory : cat
          )
        );
      }
    } catch (error) {
      console.error("Error uploading image:", error);
    } finally {
      setUploadingImage(false);
    }
  };

  // Handle tab change for languages
  const handleLanguageChange = (event) => {
    setCurrentLanguage(event.target.value);
  };

  // Get the display name for the current category based on selected language
  const getCategoryDisplayName = (category) => {
    if (!category) return "";

    const languageSpec = category.languageSpec || {};

    // Use the language-specific name if available, otherwise fall back to default name
    return languageSpec[currentLanguage]?.name || category.name;
  };

  // Get available languages for the category
  const getAvailableLanguages = (category) => {
    if (!category || !category.languageSpec) return [];

    return Object.keys(category.languageSpec).filter(
      (lang) =>
        category.languageSpec[lang]?.name || category.languageSpec[lang]?.image
    );
  };

  // Drag and Drop handlers
  const handleDragStart = (e, category) => {
    setDraggedItem(category);
    e.dataTransfer.effectAllowed = 'move';
    // Add drag image
    const dragImage = e.currentTarget.cloneNode(true);
    dragImage.style.opacity = '0.8';
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, e.clientX - e.currentTarget.getBoundingClientRect().left, 20);
    setTimeout(() => document.body.removeChild(dragImage), 0);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Auto-scroll functionality
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const rect = container.getBoundingClientRect();
      const scrollSpeed = 10;
      const scrollMargin = 50;
      
      // Get mouse position relative to container
      const mouseY = e.clientY - rect.top;
      
      // Scroll up if near top
      if (mouseY < scrollMargin) {
        container.scrollTop -= scrollSpeed;
      }
      // Scroll down if near bottom
      else if (mouseY > rect.height - scrollMargin) {
        container.scrollTop += scrollSpeed;
      }
    }
  };

  const handleDragEnter = (e, category) => {
    e.preventDefault();
    if (draggedItem && draggedItem.id !== category.id) {
      setDropTarget(category.id);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    // Only clear drop target if we're leaving the container entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDropTarget(null);
    }
  };

  const handleDrop = (e, targetCategory) => {
    e.preventDefault();
    setDropTarget(null);
    
    if (!draggedItem || draggedItem.id === targetCategory.id) {
      return;
    }

    // Create a copy of categories sorted by current priority
    const sortedCategories = [...allCategories].sort((a, b) => 
      (a.sortingPriority || 0) - (b.sortingPriority || 0)
    );

    // Find indices
    const draggedIndex = sortedCategories.findIndex(cat => cat.id === draggedItem.id);
    const targetIndex = sortedCategories.findIndex(cat => cat.id === targetCategory.id);

    // Remove dragged item and insert at new position
    const [removed] = sortedCategories.splice(draggedIndex, 1);
    sortedCategories.splice(targetIndex, 0, removed);

    // Update sorting priorities
    const updatedCategories = sortedCategories.map((cat, index) => ({
      ...cat,
      sortingPriority: index
    }));

    // Update local state
    setAllCategories(updatedCategories);
    setDraggedItem(null);

    // Save to backend
    saveSortingOrder(updatedCategories);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDropTarget(null);
  };

  // Save sorting order to backend
  const saveSortingOrder = async (categories) => {
    setSavingSortOrder(true);
    try {
      // Prepare data for bulk update
      const categoriesToUpdate = categories.map(cat => ({
        id: cat.id,
        sortingPriority: cat.sortingPriority
      }));

      // Use bulk update endpoint
      const response = await api.put('/category/bulk-update-sorting', {
        categories: categoriesToUpdate
      });

      if (response.data && response.data.success) {
        setNotification({
          open: true,
          message: 'Category order updated successfully!',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error updating sorting order:', error);
      setNotification({
        open: true,
        message: 'Failed to update category order. Please try again.',
        severity: 'error'
      });
      // Revert to original order
      fetchAllCategories();
    } finally {
      setSavingSortOrder(false);
    }
  };

  return (
    <Box>
      {/* Search Controls */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography
              variant="h5"
              sx={{ mb: 2, display: "flex", alignItems: "center" }}
            >
              <SearchIcon sx={{ mr: 1 }} /> Search Category Database
            </Typography>
            <Autocomplete
              fullWidth
              freeSolo
              options={searchResults}
              getOptionLabel={(option) => {
                // Get name from languageSpec if available
                if (
                  option.languageSpec &&
                  option.languageSpec[currentLanguage]?.name
                ) {
                  return option.languageSpec[currentLanguage].name;
                }
                return option.name || "";
              }}
              loading={loading}
              onInputChange={(e, value) => setSearchQuery(value)}
              onChange={handleCategorySelect}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  variant="outlined"
                  label="Enter category name to search"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "56px",
                      paddingRight: "14px",
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <SearchIcon color="action" sx={{ mr: 1 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              sx={{ width: "100%" }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Selected Category Display/Edit Section */}
      {selectedCategory && (
        <Paper elevation={3} sx={{ p: 3 }}>
          {!editMode ? (
            // View Mode
            <Box>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
              >
                <Typography variant="h5">{selectedCategory.name}</Typography>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={handleEditClick}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteClick}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card sx={cardStyle} elevation={2}>
                    <CardMedia
                      component="img"
                      sx={detailCardMediaStyle}
                      image={selectedCategory?.image}
                      alt={getCategoryDisplayName(selectedCategory)}
                    />
                    <CardContent sx={cardContentStyle}>
                      <Typography variant="h6">Details</Typography>
                      <Typography variant="body1">
                        <strong>ID:</strong> {selectedCategory.id}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Description:</strong> {selectedCategory.description || "N/A"}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Sorting Priority:</strong> {selectedCategory.sortingPriority || 0}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Slogan:</strong> {
                          selectedCategory.slogan && typeof selectedCategory.slogan === 'object' 
                            ? (selectedCategory.slogan[currentLanguage] || "No slogan set for this language")
                            : (selectedCategory.slogan || "No slogan set")
                        }
                      </Typography>

                      <Box sx={{ mt: 'auto', pt: 2 }}>
                        <Typography variant="h6">
                          Available Languages
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            mt: 1,
                          }}
                        >
                          {getAvailableLanguages(selectedCategory).map(
                            (lang) => (
                              <Chip
                                key={lang}
                                label={
                                  lang.charAt(0).toUpperCase() + lang.slice(1)
                                }
                                color="primary"
                                variant="outlined"
                                icon={<LanguageIcon />}
                              />
                            )
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card sx={cardStyle} elevation={2}>
                    <CardContent sx={cardContentStyle}>
                      <Typography variant="h6" gutterBottom>
                        Language Versions
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {getAvailableLanguages(selectedCategory).map((lang) => (
                          <Card key={lang} sx={{ width: "100%" }}>
                            <CardContent>
                              <Typography variant="h6">
                                {lang.charAt(0).toUpperCase() + lang.slice(1)}
                              </Typography>
                              <Typography variant="body1">
                                <strong>Name:</strong> {selectedCategory.languageSpec[lang].name}
                              </Typography>
                              {selectedCategory.languageSpec[lang].image && (
                                <Box sx={{ mt: 2 }}>
                                  <img
                                    src={selectedCategory.languageSpec[lang].image}
                                    alt={`${lang} category`}
                                    style={{ 
                                      width: "100%", 
                                      maxHeight: "200px",
                                      objectFit: "contain",
                                      borderRadius: "4px" 
                                    }}
                                  />
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          ) : (
            // Edit Mode
            <Box>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}
              >
                <Typography variant="h5">Edit Category Item</Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveLanguageName}
                  disabled={savingChanges}
                >
                  {savingChanges ? "Saving..." : "Save Changes"}
                </Button>
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Language selector moved to edit section */}
                <Grid item xs={12} md={6}>
                  <Card sx={cardStyle} elevation={2}>
                    <CardContent sx={cardContentStyle}>
                      <Typography variant="h6" gutterBottom>Language Settings</Typography>
                      <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel>Language</InputLabel>
                        <Select
                          value={currentLanguage}
                          label="Language"
                          onChange={handleLanguageChange}
                        >
                          {defaultLanguages.map((lang) => (
                            <MenuItem key={lang} value={lang}>
                              {lang.charAt(0).toUpperCase() + lang.slice(1)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <TextField
                        fullWidth
                        label={`Name (${currentLanguage})`}
                        value={
                          categoryData.languageSpec?.[currentLanguage]?.name || ""
                        }
                        onChange={(e) => handleLanguageNameChange(e.target.value)}
                        sx={{ mb: 3 }}
                      />

                      <TextField
                        fullWidth
                        label={`Category Slogan (${currentLanguage})`}
                        value={
                          (categoryData.slogan && typeof categoryData.slogan === 'object') 
                            ? (categoryData.slogan[currentLanguage] || "")
                            : ""
                        }
                        onChange={(e) => handleSloganChange(e.target.value)}
                        sx={{ mb: 3 }}
                        helperText="A catchy slogan for this category"
                      />

                      <TextField
                        fullWidth
                        label="Sorting Priority"
                        type="number"
                        value={categoryData.sortingPriority || 0}
                        onChange={(e) => setCategoryData({...categoryData, sortingPriority: parseInt(e.target.value) || 0})}
                        sx={{ mb: 3 }}
                        helperText="Lower numbers appear first"
                      />

                      <Button
                        variant="outlined"
                        component="label"
                        fullWidth
                        sx={{ height: "56px" }}
                        disabled={uploadingImage}
                      >
                        {uploadingImage
                          ? "Uploading..."
                          : `Upload ${
                              currentLanguage.charAt(0).toUpperCase() +
                              currentLanguage.slice(1)
                            } Image`}
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleImageUpload}
                        />
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card sx={cardStyle} elevation={2}>
                    <CardContent sx={cardContentStyle}>
                      <Typography variant="h6" gutterBottom>
                        Current {currentLanguage.charAt(0).toUpperCase() + currentLanguage.slice(1)} Preview
                      </Typography>
                      
                      <Typography variant="body1" gutterBottom>
                        <strong>Name:</strong> {categoryData.languageSpec?.[currentLanguage]?.name || 'Not set'}
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        <strong>Slogan:</strong> {
                          (categoryData.slogan && typeof categoryData.slogan === 'object') 
                            ? (categoryData.slogan[currentLanguage] || 'Not set')
                            : 'Not set'
                        }
                      </Typography>

                      {categoryData.languageSpec?.[currentLanguage]?.image ? (
                        <Box sx={{ mt: 'auto' }}>
                          <Typography variant="body1" gutterBottom><strong>Current Image:</strong></Typography>
                          <Box sx={{ maxWidth: '100%', overflow: 'hidden', borderRadius: '4px' }}>
                            <img
                              src={categoryData.languageSpec[currentLanguage].image}
                              alt={`${currentLanguage} category preview`}
                              style={{ 
                                width: '100%', 
                                maxHeight: '300px', 
                                objectFit: 'contain' 
                              }}
                            />
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{ 
                          mt: 'auto',
                          p: 3, 
                          bgcolor: 'background.default', 
                          borderRadius: '4px', 
                          textAlign: 'center'
                        }}>
                          <Typography variant="body2" color="text.secondary">
                            No image uploaded for this language
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>
      )}

      {/* Sorting Management Section */}
      {!selectedCategory && !loading && allCategories.length > 0 && (
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              Category Sorting Order
            </Typography>
            {savingSortOrder && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Saving...
                </Typography>
              </Box>
            )}
          </Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Drag and drop categories to reorder them. Changes are saved automatically.
          </Alert>
          <Box 
            ref={scrollContainerRef}
            sx={{ 
              maxHeight: '400px', 
              overflowY: 'auto',
              position: 'relative',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'action.hover',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'action.selected',
                borderRadius: '4px',
              },
            }}
          >
            {allCategories
              .sort((a, b) => (a.sortingPriority || 0) - (b.sortingPriority || 0))
              .map((category, index) => (
                <Box
                  key={category.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, category)}
                  onDragOver={handleDragOver}
                  onDragEnter={(e) => handleDragEnter(e, category)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, category)}
                  onDragEnd={handleDragEnd}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1,
                    mb: 1,
                    bgcolor: dropTarget === category.id ? 'action.selected' : 'background.default',
                    borderRadius: 1,
                    border: '2px solid',
                    borderColor: dropTarget === category.id ? 'primary.main' : draggedItem?.id === category.id ? 'primary.light' : 'divider',
                    cursor: 'move',
                    opacity: draggedItem?.id === category.id ? 0.5 : 1,
                    transform: dropTarget === category.id ? 'scale(1.02)' : 'scale(1)',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    '&:hover': {
                      bgcolor: draggedItem ? 'action.hover' : 'action.hover',
                      transform: draggedItem ? 'scale(1.02)' : 'translateX(4px)',
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: dropTarget === category.id ? '-4px' : '0',
                      left: 0,
                      right: 0,
                      height: '4px',
                      bgcolor: 'primary.main',
                      opacity: dropTarget === category.id ? 1 : 0,
                      transition: 'all 0.2s ease',
                    },
                  }}
                >
                  <DragIndicatorIcon sx={{ mr: 2, color: 'action.active' }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body1">
                      {category.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Priority: {category.sortingPriority || 0}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleCardClick(category)}
                    sx={{ ml: 1 }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
          </Box>
        </Paper>
      )}

      {/* Display category cards when no category is selected */}
      {!selectedCategory && !loading && searchResults.length > 0 && (
  <Paper elevation={3} sx={{ p: 3 }}>
    <Typography variant="h5" sx={{ mb: 3 }}>
      All Categories
    </Typography>
    <Grid 
      container 
      spacing={3} 
      sx={{ 
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(1, 1fr)',  // 1 card per row on extra small screens
          sm: 'repeat(2, 1fr)',  // 2 cards per row on small screens
          md: 'repeat(3, 1fr)',  // 3 cards per row on medium screens 
          lg: 'repeat(4, 1fr)'   // 4 cards per row on large screens
        },
        gap: 3,
        '& > .MuiGrid-item': {
          paddingTop: 0,
          paddingLeft: 0
        }
      }}
    >
      {searchResults.map((category) => (
        <Grid item key={category.id} sx={{ height: '100%' }}>
          <Card
            sx={{
              ...cardStyle,
              position: "relative"
            }}
            elevation={2}
            onClick={() => handleCardClick(category)}
          >
            {/* Delete button in top right corner */}
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation(); // Prevent card click
                setDeleteDialog({ open: true, category: category });
              }}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 2,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 1)",
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
            <CardMedia
              component="img"
              sx={cardMediaStyle}
              image={category.image}
              alt={getCategoryDisplayName(category)}
            />
            <CardContent sx={{
              ...cardContentStyle,
              overflow: 'hidden', // Prevent content overflow
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}>
              <Box>
                <Typography variant="h6" noWrap>
                  {getCategoryDisplayName(category)}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  mt: 'auto',
                  pt: 1
                }}
              >
                {getAvailableLanguages(category).map((lang) => (
                  <Chip
                    key={lang}
                    label={lang.charAt(0).toUpperCase() + lang.slice(1)}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  </Paper>
)}

      {/* Display message when no categories are found */}
      {!selectedCategory && !loading && searchResults.length === 0 && (
        <Paper elevation={3} sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6">
            No categories found matching your search
          </Typography>
        </Paper>
      )}

      {/* Loading indicator */}
      {loading && !selectedCategory && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. Please make sure no food items are using this category.
          </Alert>
          <Typography>
            Are you sure you want to delete the category "{deleteDialog.category?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}